import { IS_NOT_EMPTY } from 'cargo-chief-commons';
import { PLAN_NAMES } from '@domain/constants/app-variables';

import { ProductsService } from '@service/products/products.service';
import { UtilService } from '@service/util/util.service';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { environment } from '@environment/environment';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { isPaidCarrierPlan } from '../../register/utils/carrier-utils';

export interface HeaderMenu {
	DASHBOARD: boolean;
	PROJECTS: boolean;
	CAPACITY: boolean;
	NETWORK: boolean;
	SCORECARDS: boolean;
	BA_PERFORMANCE_METRICS_ENABLED: boolean;
	LOADS: boolean;
	PRICING: boolean;
	LANES: boolean;
	CUSTOMERS: boolean;
	QUICK_SEARCH: boolean;
	BIDS: boolean;
	OFFERS: boolean;
	LANE_ANALYTICS: boolean;
	LANE_CARRIER_ANALYTICS: boolean;
	WORKFLOW_PERFORMANCE_REPORT: boolean;
	STRONG_LANES_DASHBOARD: boolean;
	COVERAGE: boolean;

	// carrier nav bar menu
	CARRIER_DASHBOARD: boolean;
	CARRIER_LANES: boolean;
	CARRIER_AVAILABLE_LOADS: boolean;
	CARRIER_BOOKED_LOADS: boolean;
	CARRIER_OFFERS: boolean;
	CARRIER_QUALITY_SCORE: boolean;
	CARRIER_NETWORK: boolean;
	CARRIER_DISCOVER: boolean;
	CARRIER_PROFILE: boolean;
}

export const buildMenuOptions = (
	isPaymentView: boolean,
	showLogout: boolean,
	isFullWidthLayout: boolean,
	IS_CARRIERS_ENABLED: boolean,
	utilService: UtilService,
	productsService: ProductsService,
	accountSettings: AccountSettingsService,
	cargochief: Cargochief
) => {
	let MENU_OPTIONS: HeaderMenu = {
		DASHBOARD: false,
		PROJECTS: false,
		CAPACITY: false,
		NETWORK: false,
		SCORECARDS: false,
		LOADS: false,
		BA_PERFORMANCE_METRICS_ENABLED: false,

		PRICING: false,
		LANES: false,
		CUSTOMERS: false,
		QUICK_SEARCH: false,
		BIDS: false,
		CARRIER_NETWORK: false,
		OFFERS: false,
		LANE_ANALYTICS: false,
		LANE_CARRIER_ANALYTICS: false,
		WORKFLOW_PERFORMANCE_REPORT: false,
		STRONG_LANES_DASHBOARD: false,
		COVERAGE: false,

		CARRIER_DASHBOARD: false,
		CARRIER_LANES: false,
		CARRIER_AVAILABLE_LOADS: false,
		CARRIER_BOOKED_LOADS: false,
		CARRIER_OFFERS: false,
		CARRIER_QUALITY_SCORE: false,
		CARRIER_DISCOVER: false,
		CARRIER_PROFILE: false,
	};

	MENU_OPTIONS.DASHBOARD = getDashboardMenu(isPaymentView, utilService);
	MENU_OPTIONS.PROJECTS = getProjectsMenu(isPaymentView, utilService, productsService);
	MENU_OPTIONS.CAPACITY = getCapacityMenu(
		isPaymentView,
		showLogout,
		isFullWidthLayout,
		utilService,
		productsService
	);
	MENU_OPTIONS.NETWORK = getNetworkMenu(IS_CARRIERS_ENABLED, utilService);
	MENU_OPTIONS.SCORECARDS = getScorecardsMenu(utilService, productsService, accountSettings);
	MENU_OPTIONS.BA_PERFORMANCE_METRICS_ENABLED = accountSettings.BA_PERFORMANCE_METRICS_ENABLED;
	MENU_OPTIONS.LOADS = getLoadsMenu(
		isPaymentView,
		showLogout,
		isFullWidthLayout,
		utilService,
		productsService,
		accountSettings,
		cargochief
	);
	MENU_OPTIONS.PRICING = getPricingMenu(utilService, productsService);
	MENU_OPTIONS.LANES = getLanesMenu(utilService);
	MENU_OPTIONS.CUSTOMERS = getCustomersMenu(utilService, productsService, accountSettings);
	MENU_OPTIONS.QUICK_SEARCH = getQuickSearchMenu(IS_CARRIERS_ENABLED, utilService);
	MENU_OPTIONS.BIDS = getBidsMenu(utilService, productsService);
	MENU_OPTIONS.OFFERS = getOffersMenu(
		isPaymentView,
		showLogout,
		isFullWidthLayout,
		utilService,
		productsService,
		accountSettings
	);
	MENU_OPTIONS.LANE_ANALYTICS = getLaneAnalyticsMenu(utilService, accountSettings);
	MENU_OPTIONS.LANE_CARRIER_ANALYTICS = getLaneCarrierAnalyticsMenu(utilService, accountSettings);
	MENU_OPTIONS.WORKFLOW_PERFORMANCE_REPORT = getWorkflowPerformanceMenu(utilService, accountSettings);
	MENU_OPTIONS.STRONG_LANES_DASHBOARD = getStrongLanesDashboardMenu(utilService, accountSettings);
	MENU_OPTIONS.COVERAGE = getCoverageMenu(utilService, accountSettings);

	const isAccountTypeCarrier = utilService.isAccountTypeCarrier();
	const planName = cargochief.PLAN;
	MENU_OPTIONS.CARRIER_DASHBOARD =
		isAccountTypeCarrier && environment.FEATURE_FLAGS.CARRIER_NAVBAR_MENU.DASHBOARD;
	MENU_OPTIONS.CARRIER_LANES = isAccountTypeCarrier && isPaidCarrierPlan(planName);
	MENU_OPTIONS.CARRIER_AVAILABLE_LOADS =
		isAccountTypeCarrier && environment.FEATURE_FLAGS.CARRIER_NAVBAR_MENU.AVAILABLE_LOADS;
	MENU_OPTIONS.CARRIER_BOOKED_LOADS =
		isAccountTypeCarrier && environment.FEATURE_FLAGS.CARRIER_NAVBAR_MENU.BOOKED_LOADS;
	MENU_OPTIONS.CARRIER_OFFERS =
		isAccountTypeCarrier && environment.FEATURE_FLAGS.CARRIER_NAVBAR_MENU.OFFERS;
	MENU_OPTIONS.CARRIER_QUALITY_SCORE =
		isAccountTypeCarrier && environment.FEATURE_FLAGS.CARRIER_NAVBAR_MENU.QUALITY_SCORE;
	MENU_OPTIONS.CARRIER_NETWORK =
		isAccountTypeCarrier &&
		isPaidCarrierPlan(planName) &&
		environment.FEATURE_FLAGS.CARRIER_NAVBAR_MENU.NETWORK;
	MENU_OPTIONS.CARRIER_DISCOVER =
		isAccountTypeCarrier &&
		isPaidCarrierPlan(planName) &&
		environment.FEATURE_FLAGS.CARRIER_NAVBAR_MENU.DISCOVER;
	MENU_OPTIONS.CARRIER_PROFILE = isAccountTypeCarrier && isPaidCarrierPlan(planName);

	return MENU_OPTIONS;
};

function getDashboardMenu(isPaymentView, utilService: UtilService): boolean {
	return (
		!isPaymentView &&
		(utilService.isAccountTypeBroker() ||
			utilService.isAccountTypeShipper() ||
			utilService.isAccountTypeCarrier()) &&
		!utilService.isAccountTypeCarrier()
	);
}

function getProjectsMenu(
	isPaymentView,
	utilService: UtilService,
	productsService: ProductsService
): boolean {
	return (
		(utilService.isAccountTypeBroker() || utilService.isAccountTypeShipper()) &&
		productsService.PROJECT_CARRIERS &&
		!isPaymentView &&
		!utilService.isUserRoleAdminOrOwnerOnly()
	);
}

function getCapacityMenu(
	isPaymentView,
	showLogout,
	isFullWidthLayout,
	utilService: UtilService,
	productsService: ProductsService
): boolean {
	let plan: string = utilService.getUserPlanName();
	return (
		showLogout &&
		isFullWidthLayout &&
		productsService.CAPACITY_SEARCH &&
		(utilService.isAccountTypeBroker() || utilService.isAccountTypeShipper()) &&
		!isPaymentView &&
		!utilService.isUserRoleAdminOrOwnerOnly() &&
		IS_NOT_EMPTY(plan) &&
		plan.toLowerCase() != PLAN_NAMES.PRICING.toLowerCase()
	);
}

function getNetworkMenu(IS_CARRIERS_ENABLED, utilService: UtilService): boolean {
	return (
		IS_CARRIERS_ENABLED &&
		!utilService.isUserRoleAdminOrOwnerOnly() &&
		(utilService.isAccountTypeBroker() || utilService.isAccountTypeShipper())
	);
}

function getScorecardsMenu(
	utilService: UtilService,
	productsService: ProductsService,
	accountSettings: AccountSettingsService
): boolean {
	return (
		(utilService.isAccountTypeBroker() || utilService.isAccountTypeShipper()) &&
		environment.SCORECARDS &&
		productsService.SCORECARDS &&
		accountSettings.SCORECARDS
	);
}

/**
 * CN-8997: Booking Assistant is no longer the only reason to have loads. An account with an active TMS
 * integration has loads flowing in — booked ones especially — whether or not it runs Booking Assistant,
 * and gating the tab on BA alone left those accounts with no way to reach their own loads.
 *
 * The TMS check reads a value already in the stored /loginSetup payload rather than fetching, because
 * this menu is built synchronously: a fetch would make the tab appear after first paint.
 */
function getLoadsMenu(
	isPaymentView,
	showLogout,
	isFullWidthLayout,
	utilService: UtilService,
	productsService: ProductsService,
	accountSettings: AccountSettingsService,
	cargochief: Cargochief
): boolean {
	const hasBookingAssistant: boolean =
		productsService.BOOKING_ASSISTANT && accountSettings.BOOKING_ASSISTANT;

	// CN-8997: the clause above is NOT purely an account-type check — isUserRoleAdminOrOwnerOnly() is a
	// ROLE predicate, so a carrier account's owner satisfies it. Booking Assistant already excluded
	// carriers in practice; the new TMS branch would not, so it is restricted to broker/shipper rather
	// than handing carriers a broker-only /v2/loadsList tab.
	const hasTmsLoads: boolean =
		cargochief.HAS_ACTIVE_TMS_INTEGRATION &&
		(utilService.isAccountTypeBroker() || utilService.isAccountTypeShipper());

	return (
		showLogout &&
		isFullWidthLayout &&
		!isPaymentView &&
		(utilService.isAccountTypeBroker() ||
			utilService.isAccountTypeShipper() ||
			utilService.isUserRoleAdminOrOwnerOnly()) &&
		(hasBookingAssistant || hasTmsLoads)
	);
}

function getPricingMenu(utilService: UtilService, productsService: ProductsService): boolean {
	let plan: string = utilService.getUserPlanName();
	return (
		(IS_NOT_EMPTY(plan) && plan.toLowerCase() == PLAN_NAMES.PRICING.toLowerCase()) ||
		(utilService.isAccountTypeCarrier() &&
			productsService.PRICING &&
			productsService.RECENT_TRANSACTIONS)
	);
}

function getLanesMenu(utilService: UtilService): boolean {
	return utilService.isAccountTypeCarrier() && environment.CARRIER_LANES_VIEW;
}

function getCustomersMenu(
	utilService: UtilService,
	productsService: ProductsService,
	accountSettings: AccountSettingsService
): boolean {
	return (
		utilService.isAccountTypeBroker() &&
		environment.CUSTOMERS_VIEW &&
		productsService.CUSTOMERS &&
		accountSettings.CUSTOMERS &&
		accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS
	);
}

function getQuickSearchMenu(IS_CARRIERS_ENABLED, utilService: UtilService): boolean {
	return (
		(IS_CARRIERS_ENABLED && !utilService.isUserRoleAdminOrOwnerOnly()) ||
		(utilService.isAccountTypeCarrier() && !environment.FEATURE_FLAGS.CARRIER_QUICK_SEARCH_DISABLED)
	);
}

function getBidsMenu(utilService: UtilService, productsService: ProductsService): boolean {
	return utilService.isAccountTypeCarrier() && productsService.BOOKING_ASSISTANT;
}

function getOffersMenu(
	isPaymentView,
	showLogout,
	isFullWidthLayout,
	utilService: UtilService,
	productsService: ProductsService,
	accountSettings: AccountSettingsService
): boolean {
	return (
		showLogout &&
		isFullWidthLayout &&
		!isPaymentView &&
		(utilService.isAccountTypeBroker() ||
			utilService.isAccountTypeShipper() ||
			utilService.isUserRoleAdminOrOwnerOnly()) &&
		productsService.BOOKING_ASSISTANT &&
		accountSettings.BOOKING_ASSISTANT
	);
}

function getLaneAnalyticsMenu(
	utilService: UtilService,
	accountSettings: AccountSettingsService
): boolean {
	return (
		(utilService.isAccountTypeBroker() || utilService.isAccountTypeShipper()) &&
		environment.FEATURE_FLAGS?.LANE_ANALYSIS_DASHBOARD_ENABLED &&
		accountSettings.LANE_ANALYTICS
	);
}

function getLaneCarrierAnalyticsMenu(
	utilService: UtilService,
	accountSettings: AccountSettingsService
): boolean {
	return (
		(utilService.isAccountTypeBroker() || utilService.isAccountTypeShipper()) &&
		accountSettings.LANE_CARRIER_ANALYTICS
	);
}

function getStrongLanesDashboardMenu(
	utilService: UtilService,
	accountSettings: AccountSettingsService
): boolean {
	// CN-3255 — BROKER-only; gated by STRONG_LANES_ENABLED feature flag
	// and the existing STRONG_LANES account setting (plan entitlement).
	return (
		utilService.isAccountTypeBroker() &&
		!!environment.FEATURE_FLAGS?.STRONG_LANES_ENABLED &&
		accountSettings.STRONG_LANES
	);
}

// CN-9074 (Coverage / plan B6). BROKER-only, gated by the COVERAGE_ENABLED feature flag and the
// existing STRONG_LANES entitlement -- Coverage absorbs Strong Lanes' Top Lanes list, so the
// entitled set is the same one, and no new AccountSetting enum value is introduced (which would
// need a matching default in the deployed application.conf or /loginSetup silently returns no
// account settings at all -- CN-8712, CN-8994).
function getCoverageMenu(
	utilService: UtilService,
	accountSettings: AccountSettingsService
): boolean {
	return (
		utilService.isAccountTypeBroker() &&
		!!environment.FEATURE_FLAGS?.COVERAGE_ENABLED &&
		accountSettings.STRONG_LANES
	);
}

function getWorkflowPerformanceMenu(
	utilService: UtilService,
	accountSettings: AccountSettingsService
): boolean {
	return (
		(utilService.isAccountTypeBroker() || utilService.isAccountTypeShipper()) &&
		accountSettings.WORKFLOW_PERFORMANCE_REPORT
	);
}
